// src/pages/About.jsx
// Public "About" page. Built from the owner's design mock; all styling lives in
// styles/About.css (scoped under .about-page) rather than MUI sx, matching how Pricing.jsx
// and QuotePage.jsx were done — the mock is a large custom design with its own type scale,
// keyframes, and grid, and its stylesheet's reset fights MUI's generated styles.
//
// All copy is data below rather than inline markup, so a wording change is an edit to one
// array. Same convention as TRACKS in Pricing.jsx.
//
// No <footer> here: App.js renders the shared <HomeFooter /> on this route.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/About.css';

const PULL_QUOTES = [
  {
    // Only the first card carries the script flourish, per the mock.
    label: 'in their words',
    quote: 'Depth does not come at the expense of clarity.',
    attr: 'On the reading experience',
  },
  {
    quote: 'A curated dialogue, not a solitary endeavor.',
    attr: 'On the writing process',
  },
  {
    quote: 'Passive consumption becomes active intellectual exploration.',
    attr: 'On the reader experience',
  },
];

// Mirrors the three membership tracks on /pricing — keep the names in step with TRACKS there.
const ROLES = [
  {
    roman: 'I.',
    name: 'The Author',
    tag: 'The Visionary',
    desc: 'The primary creator. The author can write and publish independently, or leverage the platform to collaborate with a hand-selected community of reviewers, maintaining creative control while amplifying their output and reach.',
  },
  {
    roman: 'II.',
    name: 'The Reviewer',
    tag: 'The Curator',
    desc: 'A highly engaged user, often a subject-matter expert or developmental editor. Reviewers provide critical feedback and may be hired on a work-for-hire basis, with options to share in authorship royalties. Their reputation is quantified through a robust ranking system.',
  },
  {
    roman: 'III.',
    name: 'The Reader',
    tag: 'The Subscriber',
    desc: 'The avid consumer of knowledge. This user purchases and consumes literary works with the unique ability to provide feedback that may influence future editions, gaining unparalleled access to the author’s thought process.',
  },
];

// Icons are inline paths (stroked, 24x24 viewBox) rather than MUI icons — the mock's set has
// no @mui/icons-material equivalents and .tool-icon svg styles them as a group.
const TOOLS = [
  {
    name: 'The Composition Engine',
    desc: 'An advanced writing tool with version control and markup capabilities.',
    paths: ['M20 4 4 20', 'M20 4c0 4-2 6-6 8l-4 4', 'M14 12c-2 2-4 3-6 4', 'M4 20l2-6'],
  },
  {
    name: 'The Dialogue Interface',
    desc: 'A system for managing community commentary, revisions, and editorial markups.',
    paths: ['M3 5h13v9H8l-4 4V5Z', 'M21 9v9l-3-3h-6'],
  },
  {
    name: 'The Strategic Planner',
    desc: 'Tools for mapping out book timelines, plot points, and logical structures.',
    paths: ['M4 19V6l6-2 8 2v13l-8-2-6 2Z', 'M10 4v13', 'M18 6v13'],
  },
  {
    name: 'The Verification Suite',
    desc: 'Technical proofreading and editing assistance to ensure technical accuracy.',
    paths: ['M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z', 'M9 12l2 2 4-4'],
  },
  {
    name: 'The Reputation Ledger',
    desc: 'A transparent ranking and verification system for editors and reviewers.',
    paths: ['M4 21V11', 'M10 21V6', 'M16 21v-8', 'M20 21V3'],
  },
  {
    name: 'The Knowledge Vault',
    desc: 'A secure repository and archive for manuscripts and related materials.',
    rects: [{ x: 3, y: 4, width: 18, height: 16, rx: 2 }],
    circles: [{ cx: 12, cy: 12, r: 3.2 }],
    paths: ['M12 9.5v1.2'],
  },
  {
    name: 'The Production Module',
    desc: 'A customizable print selection tool, offering variable formats and specifications.',
    rects: [{ x: 4, y: 9, width: 16, height: 7, rx: 1 }],
    paths: ['M6 9V4h12v5', 'M6 16v4h12v-4'],
  },
  {
    name: 'The Visual Identity Lab',
    desc: 'Professional tools for book cover design and formatting.',
    paths: ['M12 3a9 9 0 1 0 0 18c1 0 1.6-.6 1.6-1.4 0-.4-.2-.7-.4-1-.3-.3-.4-.6-.4-1 0-.8.7-1.4 1.5-1.4H16a4 4 0 0 0 4-4c0-5-3.6-9-8-9Z'],
    circles: [{ cx: 7.5, cy: 12, r: 1 }, { cx: 9, cy: 8, r: 1 }, { cx: 14, cy: 7.5, r: 1 }],
  },
  {
    name: 'The Social Reader',
    desc: 'Integrated functionality for reading, reviewing, editing, and sharing within a trusted network.',
    circles: [{ cx: 8, cy: 9, r: 2.5 }, { cx: 17, cy: 8, r: 2 }],
    paths: ['M3.5 19c.5-3 2.2-5 4.5-5s4 2 4.5 5', 'M13.5 19c.4-2.4 1.7-4 3.6-4s3.1 1.5 3.5 4'],
  },
  {
    name: 'The Conceptual Matrix',
    desc: 'The proprietary “idea branching” tool that lets authors link and visualize related concepts within and across books.',
    circles: [{ cx: 6, cy: 6, r: 2 }, { cx: 6, cy: 18, r: 2 }, { cx: 18, cy: 12, r: 2 }],
    paths: ['M8 6h4a4 4 0 0 1 4 4', 'M8 18h4a4 4 0 0 0 4-4'],
  },
  {
    name: 'The Semantic Web',
    desc: 'Connectivity protocols that link ideas, books, and authors, creating a navigable network of related knowledge.',
    circles: [{ cx: 12, cy: 12, r: 9 }],
    paths: ['M3 12h18', 'M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3Z'],
  },
];

// Placeholder profiles by design — final leadership identities are still to be announced.
// Swap the names in here (and drop `placeholder`) once they are.
const LEADERS = [
  { name: 'Name Placeholder', role: 'Chief Executive Officer' },
  { name: 'Name Placeholder', role: 'Chief Product Officer' },
  { name: 'Name Placeholder', role: 'Head of Editorial' },
  { name: 'Name Placeholder', role: 'Head of Community' },
  { name: 'Name Placeholder', role: 'Head of Engineering' },
];

const ToolIcon = ({ tool }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {tool.rects?.map((r, i) => <rect key={`r${i}`} {...r} />)}
    {tool.circles?.map((c, i) => <circle key={`c${i}`} {...c} />)}
    {tool.paths?.map((d, i) => <path key={`p${i}`} d={d} />)}
  </svg>
);

export default function About() {
  const navigate = useNavigate();
  const { user, startCognito } = useAuth();
  const rootRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);

  // Already-logged-in visitors skip the Cognito redirect entirely and go straight in.
  const handleAuthCta = () => (user ? navigate('/dashboard') : startCognito('signup'));

  // Scroll-reveal for .reveal blocks (the mock's inline IntersectionObserver, made React-safe).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll('.reveal');

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // In-page jumps. preventDefault keeps react-router out of it, while the href is left in
  // place so middle-click and keyboard focus still behave.
  const scrollToId = (id) => (e) => {
    e.preventDefault();
    setNavOpen(false); // no-op above 800px; closes the mobile menu below it
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Mirrors the Home page's tab set/order — Home, About, Pricing, Community — rather
  // than this page's old Service/Product section jumps.
  const navItems = [
    { label: 'Home', href: '/', onClick: (e) => { e.preventDefault(); setNavOpen(false); navigate('/'); } },
    { label: 'About', href: '#top', active: true, onClick: (e) => { e.preventDefault(); setNavOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
    { label: 'Pricing', href: '/pricing', onClick: (e) => { e.preventDefault(); setNavOpen(false); navigate('/pricing'); } },
    { label: 'Community', href: '/community', onClick: (e) => { e.preventDefault(); setNavOpen(false); navigate('/community'); } },
  ];

  return (
    <div className="about-page" ref={rootRef}>
      <header className="masthead">
        <div className="glow g1" />
        <div className="glow g2" />

        <div className="wrap">
          <nav>
            {/* Plain img rather than MUI — this page's stylesheet has a universal reset that
                fights MUI's generated styles. Sized in About.css. */}
            <Link to="/" className="site-logo" aria-label="ReVisionari home">
              <img src="/images/revisionari-logo.png" alt="ReVisionari" />
            </Link>

            <div className={`nav-links${navOpen ? ' open' : ''}`} id="about-nav-links">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={item.active ? 'active' : undefined}
                  aria-current={item.active ? 'page' : undefined}
                  onClick={item.onClick}
                >
                  {item.label}
                </a>
              ))}
            </div>

            <button type="button" className="nav-cta" onClick={handleAuthCta}>
              Login/Sign up
            </button>
            <button
              type="button"
              className={`nav-toggle${navOpen ? ' open' : ''}`}
              aria-expanded={navOpen}
              aria-controls="about-nav-links"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </nav>

          <div className="hero">
            <span className="eyebrow">About ReVisionari</span>
            <span className="hero-script">the living archive</span>
            <h1>
              The convergence of <em>authority</em> and community
            </h1>
            <p className="hero-lede">
              ReVisionari transcends the traditional boundaries of publishing. We are an ecosystem
              where deep, authoritative knowledge meets dynamic community engagement, built to solve
              a fundamental problem for the modern thought leader: how to curate a lifetime of
              acquired information into a structured, immersive, and digestible literary work
              without sacrificing the rich context that defines expertise.
            </p>
            <p className="hero-lede">
              We are not merely a platform for writing; we are the infrastructure for intellectual
              legacy. ReVisionari gives profoundly knowledgeable authors the tools to transform
              static manuscripts into living documents, bridging the gap between the author and
              their most engaged audiences.
            </p>
          </div>
        </div>
      </header>

      {/* ============ THE PRODUCT ============ */}
      <section className="product-section" id="product">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">The product</span>
            <h2>The living manuscript</h2>
          </div>

          <div className="product-grid">
            <div className="product-copy reveal">
              <p>
                At ReVisionari, the product is more than a book. It&rsquo;s an interactive
                experience. The final published work is the culmination of a transparent and
                collaborative journey. The platform allows authors to load their manuscripts and, at
                their discretion, open specific sections for structured feedback from their vetted
                community, transforming the writing process from a solitary endeavor into a curated
                dialogue.
              </p>
              <p>
                The published result is accessible via our proprietary platform and made available
                through major book marketplaces. But the true value lies in the reading experience
                available exclusively through our app and website, where the text is enhanced with
                &ldquo;branching&rdquo; content: deep dives into tangential ideas, deconstructions of
                complex concepts, and direct authorial commentary.
              </p>
              <p>
                Readers can engage in topical discussions with the author, turning passive
                consumption into active intellectual exploration. The interface is engineered for
                intuitive navigation of these knowledge layers, so depth never comes at the expense
                of clarity.
              </p>
            </div>

            <div className="pull-stack">
              {PULL_QUOTES.map((q) => (
                <div className="pull-card reveal" key={q.attr}>
                  {q.label && <span className="script-label">{q.label}</span>}
                  <blockquote>{q.quote}</blockquote>
                  <div className="pull-attr">{q.attr}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE SERVICE ============ */}
      <section id="service">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">The service</span>
            <h2>A tiered model of intellectual collaboration</h2>
            <p>
              Our subscription service structures the publishing ecosystem into three distinct modes
              of engagement, each with tiered access commensurate with user involvement. Subscribers
              gain access to the full suite of platform tools and the ability to purchase physical
              books at a nominal print-plus-shipment cost.
            </p>
          </div>

          <div className="roles">
            {ROLES.map((r) => (
              <div className="role-card reveal" key={r.name}>
                <span className="roman">{r.roman}</span>
                <h3>{r.name}</h3>
                <span className="role-tag">{r.tag}</span>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>

          <p className="service-note reveal">
            Reviewers become verifiable assets in the literary supply chain: their standing is
            quantified and portable, not just a title.
          </p>
        </div>
      </section>

      {/* ============ THE TECHNICAL ARSENAL ============ */}
      <section className="tools-section" id="tools">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">The technical arsenal</span>
            <h2>Tools for the modern scribe</h2>
            <p>
              Our web and mobile application suite is designed to manage the entire lifecycle of a
              literary work, from inception to archival.
            </p>
          </div>

          <div className="tools-grid">
            {TOOLS.map((tool) => (
              <div className="tool-item reveal" key={tool.name}>
                <div className="tool-icon">
                  <ToolIcon tool={tool} />
                </div>
                <div className="tool-name">{tool.name}</div>
                <div className="tool-desc">{tool.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ LEADERSHIP ============ */}
      <section className="leadership-section" id="leadership">
        <div className="wrap">
          <div className="section-head reveal">
            <span className="eyebrow">Leadership</span>
            <h2>The team behind the ecosystem</h2>
            <p>Placeholder profiles, final leadership identities to be announced.</p>
          </div>

          <div className="leadership-grid">
            {LEADERS.map((leader) => (
              <div className="leader-card reveal" key={leader.role}>
                <div className="leader-avatar">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
                  </svg>
                </div>
                <div className="leader-name">{leader.name}</div>
                <div className="leader-role">{leader.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING STATEMENT ============ */}
      <section className="statement">
        <div className="wrap reveal">
          <span className="eyebrow">Our conviction</span>
          <span className="hero-script">a note from us</span>
          <blockquote>ReVisionari is more than a service. It is a paradigm shift.</blockquote>
          <p className="stmt-sub">
            We are building the definitive platform for the deep, collaborative construction and
            dissemination of human knowledge.
          </p>
          <div className="cta-row">
            <button type="button" className="btn-solid" onClick={() => navigate('/pricing')}>
              Explore membership
            </button>
            {/* "Book Pricing" in the ticket — that's the /quote estimator. */}
            <button type="button" className="btn-outline" onClick={() => navigate('/quote')}>
              Get a book quote
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
